import { useCallback, useMemo, useState } from 'react';

import { Box, Grid } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { ApplicationStatus } from '@prisma/client';

import { navigate } from '@redwoodjs/router';
import { MetaTags, useMutation } from '@redwoodjs/web';

import DateFilterMenuItem from 'src/components/FilterDropDown/components/DateFilterMenuItem';
import SingleValueFilterMenuItem from 'src/components/FilterDropDown/components/SingleValueFilterMenuItem';
import {
    buildFilters,
    createFilterObject,
    DateRangeDetails,
    SingleValueDetails,
} from 'src/components/FilterDropDown/utils';
import { ConfirmationModal } from 'src/components/Modals';
import {
    NPIDataGrid,
    type NPIDataGridColDef,
    type NPIDataGridRow,
} from 'src/components/NPIDataGrid/NPIDataGrid';
import StatusChip from 'src/components/StatusChip';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { DEFAULT_BORDER } from 'src/constants';
import { DELETE_MERCHANT_APPLICATION } from 'src/graphql/mutations/deleteMerchantApplication.mutation';
import { GET_MERCHANT_APPLICATIONS_PAGINATED } from 'src/graphql/queries/getMerchantApplicationsPaginated.query';
import useHasPermission from 'src/hooks/useHasPermission';
import useStablePagination from 'src/hooks/useStablePagination';
import useTable from 'src/hooks/useTable';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { enumToCamelCase } from 'src/lib/formatters';
import { resolvePageSize } from 'src/lib/pagination';
import TabMenuList from 'src/pages/MerchantServicesPage/components/TabMenuList';
import TabMenuListItem from 'src/pages/MerchantServicesPage/components/TabMenuListItem';
import { ApplicationCategory } from 'types/graphql';

const MerchantApplicationsPage = () => {
    const hasDeletePermission = useHasPermission('deleteApplication');
    const [isDeleteMode, setIsDeleteMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState<(number | string)[]>([]);
    const [isBulkConfirmOpen, setIsBulkConfirmOpen] = useState(false);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);
    const [activeTab, setActiveTab] = useState<ApplicationCategory>('IN_PROGRESS');

    const {
        rows,
        pagination,
        fetchMore,
        updateSorting,
        updateFilters,
        loading,
        sortState,
        filterState,
        reset,
    } = useTable({
        query: GET_MERCHANT_APPLICATIONS_PAGINATED,
        pagination: { page: 1, pageSize: resolvePageSize() },
        sorting: [],
        filterModel: { items: [] },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        filters: {
            applicationCategory: activeTab,
        },
    });
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [status, setStatus] = useState<ApplicationStatus | null>(null);
    const applicationStatusArray = Object.values(ApplicationStatus);

    const [deleteMerchantApplication] = useMutation(DELETE_MERCHANT_APPLICATION);

    // Map backend rows to apply enum formatting while preserving original headers
    const mappedRows = useMemo(() => {
        return rows.map((r: Record<string, unknown>) => {
            return {
                ...r,
                status: enumToCamelCase(r.status as string),
            };
        });
    }, [rows]);

    const buildColumns = (): NPIDataGridColDef[] => {
        return [
            {
                field: 'id',
                headerName: 'Application ID',
                width: 110,
            },
            {
                field: 'partnerName',
                headerName: 'Partner Name',
                width: 130,
            },
            {
                field: 'serviceType',
                headerName: 'Service Type',
                flex: 1,
                sortable: false,
                disableColumnMenu: true,
            },
            {
                field: 'merchantName',
                headerName: 'Trading Name',
                flex: 1,
            },
            {
                field: 'sellersName',
                headerName: "Seller's Name",
                flex: 1,
            },
            {
                field: 'dateSubmitted',
                headerName: 'Date Submitted',
                flex: 1,
            },
            {
                field: 'worldpayPO',
                headerName: 'Worldpay PO',
                flex: 1,
            },
            {
                field: 'status',
                headerName: 'Status',
                width: 160,
                renderCell: (params: GridRenderCellParams) => {
                    return <StatusChip status={(params?.value as string) ?? ''} />;
                },
            },
        ];
    };

    const columns = useMemo(() => {
        return buildColumns();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasDeletePermission, activeTab]);

    const exitDeleteMode = useCallback(() => {
        setIsDeleteMode(false);
        setSelectedIds([]);
    }, []);

    const enterDeleteMode = useCallback(() => {
        setIsDeleteMode(true);
    }, []);

    const openBulkConfirm = useCallback(() => {
        if (selectedIds.length === 0) {
            return;
        }
        setIsBulkConfirmOpen(true);
    }, [selectedIds.length]);

    const handleAddMerchant = useCallback(() => {
        if (!isDeleteMode) {
            navigate('/merchant/add');
        }
    }, [isDeleteMode]);

    const handleViewApplication = useCallback(
        ({ id }: { id: string }) => {
            if (!isDeleteMode) {
                navigate(`/merchant-applications/view-application/${id}`);
            }
        },
        [isDeleteMode]
    );

    const { stableTotalRows, stablePage } = useStablePagination(pagination, loading);

    const confirmBulkDelete = async () => {
        if (isBulkDeleting) {
            return;
        }
        setIsBulkDeleting(true);

        // TODO: (KM) Re-use existing single delete mutation sequentially (can be optimized later)
        try {
            for (const id of selectedIds) {
                await deleteMerchantApplication({
                    variables: { applicationId: Number(id) },
                });
            }
            showSuccessToast(`Deleted ${selectedIds.length} application(s)`);
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e) {
            showErrorToast('Error deleting selected applications');
        } finally {
            setIsBulkDeleting(false);
            setIsBulkConfirmOpen(false);
            exitDeleteMode();
            fetchMore({ page: pagination.page, pageSize: pagination.pageSize });
        }
    };

    const handleOnClearFilters = () => {
        setFromDate(null);
        setToDate(null);
        setStatus(null);
        reset();
    };

    const handleOnApplyFilters = () => {
        const dateDetails: DateRangeDetails = {
            field: 'dateSubmitted',
            fromDate: fromDate || null,
            toDate: toDate || null,
        };
        const statusDetails: SingleValueDetails = {
            field: 'status',
            value: status,
        };
        const model = createFilterObject(buildFilters(dateDetails, statusDetails));
        updateFilters(model);
    };

    return (
        <PageContent
            heading="Merchant Applications"
            subHeading="View and manage all merchant applications"
        >
            <MetaTags title="Merchant Applications" description="Merchant Applications" />
            <Grid container sx={{ gap: '24px' }}>
                <Grid item xs={12}>
                    <Box
                        flexGrow={1}
                        sx={{
                            ...DEFAULT_BORDER,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <TabMenuList>
                            <TabMenuListItem
                                isActive={activeTab === 'IN_PROGRESS'}
                                label="In-Progress"
                                onClick={() => {
                                    setActiveTab('IN_PROGRESS');
                                }}
                            />
                            <TabMenuListItem
                                isActive={activeTab === 'COMPLETED'}
                                label="Completed"
                                onClick={() => {
                                    setActiveTab('COMPLETED');
                                }}
                            />
                        </TabMenuList>
                        <NPIDataGrid
                            key="merchant-applications-grid"
                            dataTestId="merchant-applications-data-grid"
                            rows={mappedRows as unknown as NPIDataGridRow[]}
                            columns={columns}
                            getRowId={(row: { id: number }) => {
                                return row.id;
                            }}
                            serverSideFiltering={{
                                onFilterChange: updateFilters,
                                filterModel: filterState,
                            }}
                            handleView={handleViewApplication}
                            serverSideSorting={{
                                onSortChange: updateSorting,
                                sortModel: sortState,
                            }}
                            pagination={{
                                isEnabled: true,
                                mode: 'server',
                                page: stablePage - 1,
                                pageSize: resolvePageSize(pagination.pageSize),
                                totalRows: stableTotalRows,
                                onPageChange: (page: number, pageSize: number) => {
                                    fetchMore({ page: page + 1, pageSize });
                                },
                            }}
                            addFilterButtonConfig={{
                                isFilterActive:
                                    fromDate !== null || toDate !== null || status !== null,
                                handleOnClearFilters,
                                handleOnApplyFilters,
                                filters: [
                                    <DateFilterMenuItem
                                        key="merchant-application-date-filter"
                                        title="Date Added"
                                        fromDate={fromDate}
                                        toDate={toDate}
                                        handleFromDateChange={setFromDate}
                                        handleToDateChange={setToDate}
                                        onClearFilter={() => {
                                            setFromDate(null);
                                            setToDate(null);
                                        }}
                                    />,
                                    <SingleValueFilterMenuItem
                                        key="merchant-application-status-filter"
                                        title="Status"
                                        options={applicationStatusArray}
                                        value={status || ''}
                                        onChange={setStatus}
                                        onClearFilter={() => {
                                            setStatus(null);
                                        }}
                                    />,
                                ],
                            }}
                            addRecordButtonConfig={
                                activeTab === 'IN_PROGRESS'
                                    ? {
                                          addRecordButtonText: 'Add Merchant',
                                          addRecordCallBack: handleAddMerchant,
                                      }
                                    : undefined
                            }
                            handleReset={reset}
                            filtering={{ isEnabled: true }}
                            onSelectionChange={(ids: (number | string)[]) => {
                                setSelectedIds(ids);
                            }}
                            selectedRowIds={selectedIds}
                            deleteModeConfig={
                                activeTab === 'IN_PROGRESS' && {
                                    isActive: isDeleteMode,
                                    hasPermission: hasDeletePermission,
                                    onEnter: enterDeleteMode,
                                    onCancel: exitDeleteMode,
                                    onConfirm: openBulkConfirm,
                                    disableConfirm: selectedIds.length === 0,
                                }
                            }
                            overWriteDefaultStyles={{ borderRadius: '0px', border: 'none' }}
                            loading={loading}
                        />
                    </Box>
                </Grid>
            </Grid>

            <ConfirmationModal
                isOpen={isBulkConfirmOpen}
                title="Delete Applications?"
                cancelBtnVariant="back-button"
                dialogActionMarginTop={'14px'}
                message={
                    <>
                        Are you sure you want to delete {selectedIds.length} item
                        {selectedIds.length !== 1 && 's'}?
                    </>
                }
                onCancel={() => {
                    setIsBulkConfirmOpen(false);
                }}
                onConfirm={confirmBulkDelete}
                cancelText="No"
                confirmText="Yes"
                loading={isBulkDeleting}
            />
        </PageContent>
    );
};

export default MerchantApplicationsPage;
